import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { useOptionalAuth } from '../../contexts/AuthContext';
import matchAlertService from '../../services/matchAlertService';
import { appAlert } from '../alerts/appAlert';
import { apiErrorMessage } from '../../services/api';
import { useConfirm } from '../../hooks/useConfirm';
import { t } from '../../i18n';

const ACCENT = '#3fced1';

/** Status-Werte, für die keine Glocke mehr angeboten wird */
const CLOSED_STATUS = new Set(['finished', 'cancelled', 'postponed']);

export interface MatchAlertButtonProps {
  /** Mongo-_id des ProEvents (Spiele-Listen) */
  eventId?: string | null;
  /** FlashLive-Event-ID (Explorer-/Saisonlisten) */
  flashliveEventId?: string | null;
  /** Spielstatus; bei beendet/abgesagt/verschoben wird nichts gerendert */
  status?: string | null;
  size?: number;
  className?: string;
  /** Zusätzliche Klassen, wenn die Glocke aktiv ist */
  activeClassName?: string;
}

/** Liest den Glocken-Stand für die IDs aus dem Store (re-rendert bei Änderungen) */
export function useMatchAlert(keys: Array<string | null | undefined>) {
  const auth = useOptionalAuth();
  const token = auth?.token ?? null;
  useEffect(() => {
    void matchAlertService.load(token).catch(() => undefined);
  }, [token]);
  const version = useSyncExternalStore(matchAlertService.subscribe, matchAlertService.getVersion, matchAlertService.getVersion);
  const active = matchAlertService.isActive(keys);
  return { active, version, isAuthenticated: Boolean(auth?.isAuthenticated) };
}

/**
 * Glocke je Pro-Spiel (Marcel 2026-10-24, wie bei OneFootball): aktiv = gefüllte Glocke in Türkis,
 * inaktiv = Umriss. Nur eingeloggte Nutzer; Gäste bekommen einen zentrierten Hinweis.
 * Die Pushes kommen in der DartCloud-App an – ohne verknüpftes App-Gerät sagt der Hinweis das.
 * Der Button darf NICHT in einen Link verschachtelt werden – als Geschwister absolut über die Karte legen.
 */
const MatchAlertButton: React.FC<MatchAlertButtonProps> = ({ eventId, flashliveEventId, status, size = 16, className = '', activeClassName = '' }) => {
  const key = flashliveEventId || eventId || null;
  const { active, isAuthenticated } = useMatchAlert([eventId, flashliveEventId]);
  const [busy, setBusy] = useState(false);
  // Deaktivieren nur mit Rückfrage (Marcel 2026-10-24), wie beim Entfolgen
  const { confirm, confirmDialog } = useConfirm();

  const toggle = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!key || busy) return;
      if (!isAuthenticated) {
        appAlert.info(t('web.component.match-alert-button.anmelden-hinweis'), t('web.component.match-alert-button.match-alarm'));
        return;
      }
      const next = !active;
      if (!next) {
        const ok = await confirm({
          title: t('web.component.match-alert-button.wirklich-deaktivieren'),
          message: t('web.component.match-alert-button.deaktivieren-hint'),
          confirmLabel: t('web.component.match-alert-button.deaktivieren-kurz'),
          danger: true,
        });
        if (!ok) return;
      }
      setBusy(true);
      try {
        const result = await matchAlertService.setAlert(key, next);
        if (result.active) {
          if (result.hasDevice) appAlert.success(t('web.component.match-alert-button.aktiv-hinweis'), t('web.component.match-alert-button.match-alarm'));
          else appAlert.info(t('web.component.match-alert-button.aktiv-ohne-geraet'), t('web.component.match-alert-button.match-alarm'));
        }
        // Deaktivieren: Rückfrage war die Bestätigung, die graue Glocke zeigt den Zustand – kein zweites Popup
      } catch (err) {
        appAlert.error(apiErrorMessage(err, t('web.component.match-alert-button.fehler')));
      } finally {
        setBusy(false);
      }
    },
    [key, busy, isAuthenticated, active, confirm],
  );

  if (!key || (status && CLOSED_STATUS.has(String(status).toLowerCase()))) return null;

  const label = active ? t('web.component.match-alert-button.deaktivieren') : t('web.component.match-alert-button.aktivieren');
  const Icon = active ? BellRing : Bell;
  return (
    <>
    {confirmDialog}
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="match-alert-button"
      data-active={active ? '1' : '0'}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={`inline-flex items-center justify-center rounded-full transition-colors disabled:opacity-60 ${
        active ? `text-[#3fced1] ${activeClassName}` : 'text-gray-400 hover:text-[#3fced1]'
      } ${className}`}
    >
      <Icon size={size} fill={active ? ACCENT : 'none'} />
    </button>
    </>
  );
};

export default MatchAlertButton;
